import { legal } from '../lib/market';
import {
  accessOf,
  ACTIVITIES,
  ACTIVITY_GROUPS,
  cleanCui,
  countryOptions,
  cuiProblem,
  legalLabels,
  isBuildActivity,
  SITE_TERMS,
  SITE_TERM_IDS,
  siteTermForActivity,
  type Company,
  t,
} from '@procumi/shared';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Avatar } from '../components/Avatar';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { ask } from '../lib/confirm';
import { TeamSection } from '../components/TeamSection';
import { PlanSection } from '../components/PlanSection';
import { ApprovalSection } from '../components/ApprovalSection';
import { DataExportSection } from '../components/DataExportSection';
import { rich } from '../lib/rich';

const CUI_MSG = {
  format: 'Un CUI are între 2 și 10 cifre, cu sau fără RO în față.',
  control: t(
    'Ultima cifră (cifra de control) nu se potrivește cu celelalte. Verifică-l pe o factură sau pe anaf.ro.',
  ),
} as const;

type Fields = Pick<Company, 'name' | 'country' | 'activity' | 'siteTerm' | 'cui' | 'regCom' | 'address'>;

const fieldsOf = (c: Company): Fields => ({
  name: c.name,
  country: c.country,
  activity: c.activity,
  siteTerm: c.siteTerm,
  cui: c.cui,
  regCom: c.regCom,
  address: c.address,
});

/**
 * Datele firmei, într-un singur loc. Până la ecranul ăsta se completau din
 * treacăt: semnătura la prima cerere, datele legale la prima comandă. Acolo
 * rămân și acum (nu te trimitem aici în mijlocul unei comenzi), dar aici le
 * găsești când vrei să le corectezi.
 *
 * Echipa stă tot aici, pe a doua filă: ține de firmă, nu de munca de zi cu zi.
 * Filele rămân montate amândouă, ca o modificare nesalvată să nu se piardă
 * când te uiți la colegi.
 */
export function CompanyPage({
  company,
  toast,
  onSaved,
  onChoosePlan,
  initialTab,
  isAdmin,
}: {
  initialTab?: 'data' | 'team' | 'plan' | 'approvals';
  /** doar administratorul schimbă datele firmei, echipa și pachetul */
  isAdmin: boolean;
  company: Company;
  toast: Toast;
  onSaved: (c: Company) => void;
  onChoosePlan: () => void;
}) {
  const [f, setF] = useState<Fields>(() => fieldsOf(company));
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'data' | 'team' | 'plan' | 'approvals'>(initialTab ?? 'data');
  const set = (k: keyof Fields, v: string) => setF({ ...f, [k]: v });

  const saved = fieldsOf(company);
  const changed = (Object.keys(f) as (keyof Fields)[]).filter((k) => f[k] !== saved[k]);
  // etichetele după țara din formular, ca să se vadă pe loc ce schimbi
  const L = legalLabels(f.country);
  const countries = useMemo(countryOptions, []);
  const cuiIssue = f.cui.trim() && L.checkCui ? cuiProblem(f.cui) : '';

  const save = async () => {
    if (!f.name.trim()) return toast(t('Completează numele firmei'));
    if (cuiIssue === 'format') return toast(CUI_MSG.format);
    // Ca la furnizori: cifra de control greșită e aproape mereu o greșeală de
    // tastare, dar omul poate ști mai bine. Întrebăm, nu blocăm.
    if (
      cuiIssue === 'control' &&
      f.cui !== saved.cui &&
      !(await ask({
        title: t('CUI-ul pare greșit'),
        message: t(
          '{0}: ultima cifră (cifra de control) nu se potrivește cu celelalte, de obicei semn că o cifră e tastată greșit. Dacă știi că e corect, îl poți salva așa.',
          cleanCui(f.cui),
        ),
        confirm: t('Salvează așa'),
        cancel: t('Corectez'),
      }))
    ) {
      return;
    }
    setBusy(true);
    try {
      const body = Object.fromEntries(changed.map((k) => [k, f[k].trim()]));
      const fresh = await api.updateCompany(body);
      onSaved(fresh);
      setF(fieldsOf(fresh));
      toast(t('Datele firmei au fost salvate'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <CompanyHero company={company} onTeam={() => setTab('team')} onPlan={() => setTab('plan')} />

      <div className="tabs company-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          id="tab-data"
          aria-controls="panel-data"
          aria-selected={tab === 'data'}
          onClick={() => setTab('data')}
        >
          {iconDate}
          {t('Datele firmei')}
        </button>
        <button
          type="button"
          role="tab"
          id="tab-team"
          aria-controls="panel-team"
          aria-selected={tab === 'team'}
          onClick={() => setTab('team')}
        >
          {iconEchipa}
          {t('Echipa')}
        </button>
        <button
          type="button"
          role="tab"
          id="tab-plan"
          aria-controls="panel-plan"
          aria-selected={tab === 'plan'}
          onClick={() => setTab('plan')}
        >
          {iconPachet}
          {t('Pachet')}
        </button>
        <button
          type="button"
          role="tab"
          id="tab-approvals"
          aria-controls="panel-approvals"
          aria-selected={tab === 'approvals'}
          onClick={() => setTab('approvals')}
        >
          {iconAprobari}
          {t('Aprobări')}
        </button>
      </div>

      <div id="panel-approvals" role="tabpanel" aria-labelledby="tab-approvals" hidden={tab !== 'approvals'}>
        {tab === 'approvals' ? (
          <ApprovalSection
            toast={toast}
            isAdmin={isAdmin}
            onChoosePlan={isAdmin ? onChoosePlan : undefined}
          />
        ) : null}
      </div>

      <div id="panel-plan" role="tabpanel" aria-labelledby="tab-plan" hidden={tab !== 'plan'}>
        {tab === 'plan' ? (
          <PlanSection company={company} onChoose={isAdmin ? onChoosePlan : undefined} toast={toast} />
        ) : null}
      </div>

      <div id="panel-team" role="tabpanel" aria-labelledby="tab-team" hidden={tab !== 'team'}>
        <p className="company-intro">
          {t(
            'Toată lumea din Achiziții și Aprobare vede aceleași cereri și furnizori. Șefii de șantier trimit doar necesarul și nu ocupă loc în pachet.',
          )}
        </p>
        <TeamSection toast={toast} isAdmin={isAdmin} />
      </div>

      <form
        id="panel-data"
        role="tabpanel"
        aria-labelledby="tab-data"
        hidden={tab !== 'data'}
        className="company-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {isAdmin ? null : (
          <p className="read-warn" style={{ margin: 0 }}>
            {t('Doar un administrator al contului poate schimba datele firmei.')}
          </p>
        )}
        <fieldset disabled={!isAdmin} className="company-fields">
          <SetRow
            title={t('Datele firmei')}
            hint={t('Apar în antetul fiecărei comenzi de achiziție, ca firma care cumpără.')}
          >
            <label className="f" htmlFor="co-name">
              {t('Numele firmei')}
              <input
                id="co-name"
                className="inp"
                value={f.name}
                onChange={(e) => set('name', e.target.value)}
                maxLength={160}
              />
            </label>
            <label className="f" htmlFor="co-country">
              {t('Țara')}
              <select
                id="co-country"
                className="inp"
                value={f.country}
                onChange={(e) => set('country', e.target.value)}
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="row">
              <label className="f" htmlFor="co-activity">
                {t('Domeniul')}
                <select
                  id="co-activity"
                  className="inp"
                  value={f.activity}
                  // domeniul nou aduce și cuvântul lui; se poate schimba apoi alături
                  onChange={(e) =>
                    setF({ ...f, activity: e.target.value, siteTerm: siteTermForActivity(e.target.value) })
                  }
                >
                  <option value="">{t('Nespus')}</option>
                  {ACTIVITY_GROUPS.map((g) => (
                    <optgroup key={g.id} label={g.label}>
                      {ACTIVITIES.filter((a) => a.group === g.id).map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <span className="hint">{t('Alege categoriile de furnizori propuse.')}</span>
              </label>
              <label className="f" htmlFor="co-site-term">
                {t('Unde ajunge marfa')}
                <select
                  id="co-site-term"
                  className="inp"
                  value={f.siteTerm}
                  onChange={(e) => set('siteTerm', e.target.value)}
                >
                  {SITE_TERM_IDS.filter(
                    (id) => (id === 'santier') === isBuildActivity(f.activity) || id === 'proiect',
                  ).map((id) => (
                    <option key={id} value={id}>
                      {t(SITE_TERMS[id].label)}
                    </option>
                  ))}
                </select>
                <span className="hint">
                  {t('Cum apare în meniu, pe cereri și în mailurile către furnizori.')}
                </span>
              </label>
            </div>
            <div className="row">
              <label className="f" htmlFor="co-cui">
                {L.taxId}
                <input
                  id="co-cui"
                  className="inp"
                  value={f.cui}
                  placeholder={L.checkCui ? 'RO12345678' : undefined}
                  maxLength={20}
                  aria-invalid={cuiIssue !== ''}
                  onChange={(e) => set('cui', e.target.value)}
                />
                {cuiIssue ? <span className="hint warn-text">{CUI_MSG[cuiIssue]}</span> : null}
              </label>
              <label className="f" htmlFor="co-regcom">
                {L.reg}
                <input
                  id="co-regcom"
                  className="inp"
                  value={f.regCom}
                  placeholder={L.checkCui ? 'J08/123/2020' : undefined}
                  maxLength={40}
                  onChange={(e) => set('regCom', e.target.value)}
                />
              </label>
            </div>
            <label className="f" htmlFor="co-address">
              {t('Adresa sediului')}
              <input
                id="co-address"
                className="inp"
                value={f.address}
                maxLength={300}
                onChange={(e) => set('address', e.target.value)}
              />
            </label>
          </SetRow>

          <LogoCard company={company} toast={toast} onSaved={onSaved} />

          <SetRow
            title={t('Semnătura mailurilor')}
            hint={t('La finalul cererilor de ofertă, al follow-up-urilor și al comenzilor către furnizori.')}
          >
            <p className="hint" style={{ margin: 0 }}>
              {rich(
                'Semnătura e acum a fiecărui om, nu a firmei: fiecare și-o pune în <b>Profilul meu</b> (clic pe nume, jos în stânga), ca o carte de vizită cu logo-ul firmei sau cea din Outlook.',
              )}
            </p>
          </SetRow>

          {changed.length ? (
            <div className="save-bar" role="status">
              <span>
                {t('Ai modificări nesalvate')}
                <small>
                  {changed.length === 1 ? t('1 câmp schimbat') : t('{0} câmpuri schimbate', changed.length)}
                </small>
              </span>
              <button type="button" className="btn" disabled={busy} onClick={() => setF(saved)}>
                {t('Renunță')}
              </button>
              <button type="submit" className="btn primary" disabled={busy}>
                {busy ? t('Se salvează…') : t('Salvează')}
              </button>
            </div>
          ) : null}
        </fieldset>
      </form>
      {tab === 'data' && isAdmin ? <DataExportSection toast={toast} /> : null}
    </>
  );
}

/** Cât acceptă serverul; verificat și aici, ca să nu urce degeaba o poză de 5 MB. */
const MAX_LOGO_KB = 512;

/**
 * Logo-ul, pentru antetul comenzilor de achiziție. Se salvează pe loc, separat
 * de restul formularului: o imagine aleasă nu e o modificare de „salvat”.
 */
function LogoCard({
  company,
  toast,
  onSaved,
}: {
  company: Company;
  toast: Toast;
  onSaved: (c: Company) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const url = api.logoUrl(company);

  const pick = async (file: File | undefined) => {
    if (input.current) input.current.value = '';
    if (!file) return;
    if (file.size > MAX_LOGO_KB * 1024) {
      toast(t('Imaginea are {0} KB. Maximul e {1} KB.', Math.round(file.size / 1024), MAX_LOGO_KB));
      return;
    }
    setBusy(true);
    try {
      onSaved(await api.uploadLogo(file));
      toast(t('Logo-ul a fost salvat'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const ok = await ask({
      title: t('Ștergi logo-ul?'),
      message: t('Comenzile, inclusiv cele emise deja, se vor tipări fără logo.'),
      confirm: t('Șterge logo-ul'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      onSaved(await api.deleteLogo());
      toast(t('Logo-ul a fost șters'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SetRow
      title={t('Logo')}
      hint={t('Apare sus pe comenzile de achiziție și pe paginile pe care le văd furnizorii.')}
    >
      <div>
        <p className="hint">
          {t(
            'Apare în colțul comenzilor de achiziție, pe ecran și în PDF. PNG, JPG sau WebP, cel mult {0} KB. Un PNG cu fundal transparent arată cel mai bine.',
            MAX_LOGO_KB,
          )}
        </p>
        <div className="logo-row">
          <div className={`logo-box${url ? '' : ' empty'}`}>
            {url ? <img src={url} alt={t('Logo {0}', company.name)} /> : <span>{t('fără logo')}</span>}
          </div>
          <div className="logo-acts">
            <input
              ref={input}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => void pick(e.target.files?.[0])}
            />
            <button type="button" className="btn" disabled={busy} onClick={() => input.current?.click()}>
              {busy ? t('Se salvează…') : url ? t('Schimbă logo-ul') : t('Alege imaginea')}
            </button>
            {url ? (
              <button type="button" className="btn" disabled={busy} onClick={() => void remove()}>
                {t('Șterge')}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </SetRow>
  );
}

/** O secțiune de setări, ca la Stripe: titlul și rostul în stânga, câmpurile în dreapta. */
function SetRow({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <div className="set-row">
      <div className="set-l">
        <h2>{title}</h2>
        <p>{hint}</p>
      </div>
      <section className="sheet set-r">{children}</section>
    </div>
  );
}

/**
 * Fișa firmei, sus pe pagină: logo, nume, datele legale, pachetul și cât din el
 * folosiți (colegi, șantiere active), cu avatarele echipei.
 */
function CompanyHero({
  company,
  onTeam,
  onPlan,
}: {
  company: Company;
  onTeam: () => void;
  onPlan: () => void;
}) {
  const team = useAsync(() => api.team(), [], 'team');
  const sites = useAsync(() => api.sites(), [], 'sites');
  const access = accessOf(company);
  const plan = access.plan;
  const logo = api.logoUrl(company);
  const L = legalLabels(company.country);
  const people = (team.data?.users ?? []).filter((u) => !u.deactivatedAt);
  const seats = people.filter((u) => u.role !== 'site').length;
  const active = (sites.data ?? []).filter((s) => !s.closedAt).length;
  const facts = [
    company.cui ? `${L.taxId} ${company.cui}` : null,
    company.regCom || null,
    company.address || null,
  ].filter(Boolean);

  return (
    <section className="co-hero sheet">
      <div className="co-id">
        <div className={`co-logo${logo ? '' : ' empty'}`}>
          {logo ? <img src={logo} alt="" /> : <span>{company.name.slice(0, 2).toUpperCase()}</span>}
        </div>
        <div className="co-t">
          <h1>{company.name}</h1>
          <p>
            {facts.length
              ? facts.join(' · ')
              : t('Completează {0}-ul și adresa: apar pe comenzi.', legal().taxId)}
          </p>
        </div>
        <button
          type="button"
          className={`co-plan${access.trialDaysLeft != null ? ' trial' : ''}`}
          onClick={onPlan}
        >
          {plan ? t('Pachet {0}', plan.name) : t('Fără pachet')}
          {access.trialDaysLeft != null ? (
            <small>
              {t(
                'probă, {0} {1}',
                access.trialDaysLeft,
                access.trialDaysLeft === 1 ? t('zi rămasă') : t('zile rămase'),
              )}
            </small>
          ) : null}
        </button>
      </div>
      <div className="co-stats">
        <button type="button" className="co-stat" onClick={onTeam}>
          <span>{t('Colegi')}</span>
          <b>
            {seats}
            {plan?.users != null ? <small> {t('din {0}', plan.users)}</small> : null}
          </b>
          {plan?.users != null ? <Meter used={seats} of={plan.users} /> : null}
        </button>
        <div className="co-stat">
          <span>{t('{0} active', t('Șantiere'))}</span>
          <b>
            {active}
            {plan?.sites != null ? (
              <small> {t('din {0}', plan.sites)}</small>
            ) : (
              <small> {t('· nelimitat')}</small>
            )}
          </b>
          {plan?.sites != null ? <Meter used={active} of={plan.sites} /> : null}
        </div>
        <button type="button" className="co-stat co-people" onClick={onTeam}>
          <span>{t('Echipa')}</span>
          <span className="avs">
            {people.slice(0, 6).map((u) => (
              <Avatar key={u.id} name={u.name} userId={u.id} avatarAt={u.avatarAt} />
            ))}
            {people.length > 6 ? <span className="more">+{people.length - 6}</span> : null}
          </span>
        </button>
      </div>
    </section>
  );
}

/** Cât din limita pachetului e folosit; portocaliu de la 80%, ca să nu te prindă nepregătit. */
function Meter({ used, of }: { used: number; of: number }) {
  const pct = Math.min(100, Math.round((used / Math.max(of, 1)) * 100));
  return (
    <span className={`co-meter${pct >= 80 ? ' hi' : ''}`} aria-hidden="true">
      <i style={{ width: `${pct}%` }} />
    </span>
  );
}

/* Iconițele filelor: caroiaj de 16, contur. */
const ico = (d: string) => (
  <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d={d}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const iconDate = ico(
  'M3 14.2V3.4A1.2 1.2 0 0 1 4.2 2.2h7.6A1.2 1.2 0 0 1 13 3.4v10.8M1.6 14.2h12.8M6.6 14.2v-3h2.8v3M5.6 5.2h1.2M9.2 5.2h1.2M5.6 8h1.2M9.2 8h1.2',
);
const iconEchipa = ico(
  'M6 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM1.5 13.5c.6-2.3 2.3-3.5 4.5-3.5s3.9 1.2 4.5 3.5M10.5 2.7a2.5 2.5 0 0 1 0 4.6M12.2 10.3c1.2.5 2 1.6 2.3 3.2',
);
const iconPachet = ico('M8 1.8 13.5 4.6v6.8L8 14.2 2.5 11.4V4.6zM2.5 4.6 8 7.4l5.5-2.8M8 7.4v6.8');
const iconAprobari = ico('M2.5 8.5 6 12l7.5-8');
